/**
 * Trims whitespace from the beginning and end of a string.
 *
 * @param {string} value - The string to trim.
 * @returns {string} The trimmed string, or the original value if it is not a string.
 */
const trim = (value) => (typeof value === "string" ? value.trim() : value);

/**
 * Validates if the given value is a valid phone area code.
 * The area code must not start with a 0 or 1.
 *
 * @param {string} value - The phone area code to validate.
 * @returns {Object} An object containing:
 *   - {boolean} valid - Indicates if the area code is valid.
 *   - {string} message - A message indicating the validation result.
 */
export const validatePhoneAreacode = (value) => {
  const phoneRegEx = /^(?:[2-9][0-9]{2}|\([2-9][0-9]{2}\))/g;
  return {
    valid: phoneRegEx.test(trim(value)),
    message: "Please enter a valid area code. The area code must not start with a 0 or 1."
  };
};

/**
 * Validates a phone number to ensure it is a valid 10 digit number.
 *
 * @param {string} value - The phone number to validate.
 * @returns {Object} An object containing:
 *   - {boolean} valid - Whether the phone number is valid.
 *   - {string} message - A message indicating the validation result.
 */
export const validatePhone = (value) => {
  const phoneRegEx = /^(?:[2-9][0-9]{2}|\([2-9][0-9]{2}\))\s?-?[0-9]{3}\s?-?[0-9]{4}$/g;
  return {
    valid: phoneRegEx.test(trim(value)),
    message: "Please enter a valid 10 digit phone number. E.g. 321-555-7890."
  };
};

/**
 * Validates the user cookie object to ensure it contains the required fields
 * and that they meet specific criteria.
 *
 * @param {Object} userCookie - The user cookie object to validate.
 * @param {string} userCookie.handshake - A non-empty string representing the handshake.
 * @param {string} userCookie.marker - A non-empty string representing the marker.
 * @param {number|string} userCookie.supporter_number - A numeric value representing the supporter number.
 * @param {string} userCookie.email - A valid email string.
 * @returns {boolean} - Returns true if the user cookie is valid, otherwise false.
 */
export const validateUserCookie = (userCookie) => {
  if (!userCookie) {
    return false;
  }

  const { handshake, marker, supporter_number, email } = userCookie;

  // Check if the "supporter_number" exists and is a numeric value
  if (!/^\d+$/.test(`${supporter_number}`)) {
    return false;
  }

  // Check if the "email" exists and is a valid email string
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (typeof email !== "string" || !emailRegex.test(email)) {
    return false;
  }

  // Check if the "handshake" exists and is a non-empty string
  if (typeof handshake !== "string" || handshake.trim() === "") {
    return false;
  }

  // Check if the "marker" exists and is a non-empty string
  if (typeof marker !== "string" || marker.trim() === "") {
    return false;
  }

  // If all checks pass, return true
  return true;
};

/**
 * Validates the supporter data against the user cookie.
 *
 * @param {Object} supporterData - The data of the supporter to validate.
 * @param {string} supporterData.firstName - The first name of the supporter.
 * @param {string} supporterData.lastName - The last name of the supporter.
 * @param {string|number} supporterData.supporterNumber - The supporter number.
 * @param {Object} userCookie - The user cookie containing supporter information.
 * @param {string|number} userCookie.supporter_number - The supporter number from the cookie.
 * @returns {boolean} - Returns true if the supporter data is valid, otherwise false.
 * @throws {Error} - Throws an error if the supporter number does not match.
 */
export const validateSupporterData = (supporterData, userCookie) => {
  if (!supporterData || !userCookie || !userCookie.supporter_number) {
    return false;
  }

  const { firstName, lastName, email, supporterNumber } = supporterData;

  if (!firstName || !lastName || !email || !supporterNumber) {
    return false;
  }

  if (`${userCookie.supporter_number}` !== `${supporterNumber}`) {
    throw new Error("Supporter Number Mismatch");
  }

  return true;
};
